import { useCustom } from '@refinedev/core';
import { CreditCard, KeyRound, ScrollText, Wallet } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

interface AccountListResponse {
  data: Array<{ id: string; status: string; region: string }>;
  count: number;
}

interface TopupListResponse {
  data: Array<{ id: string; status: string; face_value: number }>;
  count: number;
}

interface AuditListResponse {
  data: Array<{ id: string; action: string; created_at: string }>;
  count: number;
}

export function DashboardPage() {
  const { data: accounts } = useCustom<AccountListResponse>({
    url: '/psn-accounts',
    method: 'get',
    config: { query: { limit: 1 } },
  });

  const { data: available } = useCustom<AccountListResponse>({
    url: '/psn-accounts',
    method: 'get',
    config: { query: { status: 'available', limit: 1 } },
  });

  const { data: inUse } = useCustom<AccountListResponse>({
    url: '/psn-accounts',
    method: 'get',
    config: { query: { status: 'in_use', limit: 1 } },
  });

  const { data: topupAvail } = useCustom<TopupListResponse>({
    url: '/topup',
    method: 'get',
    config: { query: { status: 'available', limit: 1 } },
  });

  const { data: audit } = useCustom<AuditListResponse>({
    url: '/audit-log',
    method: 'get',
    config: { query: { limit: 5 } },
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Visão geral</h1>
        <p className="text-muted-foreground">Visão geral do pool de contas PSN.</p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={<KeyRound className="h-4 w-4 text-muted-foreground" />}
          label="Contas total"
          value={accounts?.data.count}
        />
        <StatCard
          icon={<KeyRound className="h-4 w-4 text-primary" />}
          label="Disponíveis"
          value={available?.data.count}
          accent
        />
        <StatCard
          icon={<KeyRound className="h-4 w-4 text-muted-foreground" />}
          label="Em uso"
          value={inUse?.data.count}
        />
        <StatCard
          icon={<CreditCard className="h-4 w-4 text-muted-foreground" />}
          label="Gift cards disponíveis"
          value={topupAvail?.data.count}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <ScrollText className="h-4 w-4" />
            Últimas ações
          </CardTitle>
          <CardDescription>5 entradas mais recentes do audit log.</CardDescription>
        </CardHeader>
        <CardContent>
          {audit?.data.data.length ? (
            <ul className="space-y-2 text-sm">
              {audit.data.data.map((entry) => (
                <li key={entry.id} className="flex justify-between border-b border-border/50 pb-2 last:border-0">
                  <span className="font-mono text-muted-foreground">{entry.action}</span>
                  <span className="text-xs text-muted-foreground">
                    {new Date(entry.created_at).toLocaleString('pt-BR')}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">Sem ações registradas ainda.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function StatCard({
  icon,
  label,
  value,
  accent,
}: {
  icon: React.ReactNode;
  label: string;
  value: number | undefined;
  accent?: boolean;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{label}</CardTitle>
        {icon}
      </CardHeader>
      <CardContent>
        <div className={accent ? 'text-3xl font-bold text-primary' : 'text-3xl font-bold'}>
          {value ?? <Wallet className="h-7 w-7 animate-pulse text-muted-foreground" />}
        </div>
      </CardContent>
    </Card>
  );
}
